/**
 * useFieldDraft — React hook for workspace draft CRUD via /api/sp-drafts.
 *
 * Manages loading, saving (optimistic-locked), and publishing drafts.
 * Used by FieldDraftPane in workspace mode.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import apiClient from '../services/api';

export interface FieldDraft {
  id: string;
  sub_project_id: string;
  solution_id: string | null;
  context_field: string;
  body_html: string;
  provenance: Array<{ paragraph_hash: string; sources: unknown[] }>;
  version: number;
  created_at: string;
  last_modified_at: string;
  last_modified_by: string | null;
}

interface UseFieldDraftOptions {
  subProjectId: string;
  field: string;
  solutionId?: string | null;
}

export function useFieldDraft({ subProjectId, field, solutionId }: UseFieldDraftOptions) {
  const [draft, setDraft] = useState<FieldDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load draft
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = solutionId ? `?solution_id=${encodeURIComponent(solutionId)}` : '';
      const res = await apiClient.get<FieldDraft>(`/sp-drafts/${subProjectId}/${field}${params}`);
      if (mountedRef.current) {
        setDraft(res.data);
        setDirty(false);
      }
    } catch (e: unknown) {
      if (mountedRef.current) {
        setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || String(e));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [subProjectId, field, solutionId]);

  useEffect(() => { load(); }, [load]);

  // Update local body (marks dirty)
  const updateBody = useCallback((body: string) => {
    setDraft(prev => prev ? { ...prev, body_html: body } : null);
    setDirty(true);
  }, []);

  // Save (PUT)
  const save = useCallback(async () => {
    if (!draft || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      const params = solutionId ? `?solution_id=${encodeURIComponent(solutionId)}` : '';
      const res = await apiClient.put<{ version: number }>(
        `/sp-drafts/${subProjectId}/${field}${params}`,
        { body_html: draft.body_html, version: draft.version },
      );
      if (mountedRef.current) {
        setDraft(prev => prev ? { ...prev, version: res.data.version } : null);
        setDirty(false);
      }
    } catch (e: unknown) {
      if (mountedRef.current) {
        setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || String(e));
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [draft, dirty, subProjectId, field, solutionId]);

  // Publish
  const publish = useCallback(async () => {
    if (!draft || dirty) return;
    setPublishing(true);
    setError(null);
    try {
      const params = solutionId ? `?solution_id=${encodeURIComponent(solutionId)}` : '';
      await apiClient.post(`/sp-drafts/${subProjectId}/${field}/publish${params}`);
      if (mountedRef.current) setPublishing(false);
      return true;
    } catch (e: unknown) {
      if (mountedRef.current) {
        setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || String(e));
        setPublishing(false);
      }
      return false;
    }
  }, [draft, dirty, subProjectId, field, solutionId]);

  // Reload (after external draft_updated event from WS)
  const reload = useCallback(() => { load(); }, [load]);

  return {
    draft,
    loading,
    saving,
    publishing,
    dirty,
    error,
    updateBody,
    save,
    publish,
    reload,
  };
}
